/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import type { Product } from '@shared/types/products';
import { Pencil, Trash2, ExternalLink, Github } from 'lucide-react';

interface ProductCardProps {
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  isActive?: boolean;
  onSetActive?: () => void;
  localPath?: string;
}

export function ProductCard({ product, onEdit, onDelete, isActive = false, onSetActive, localPath }: ProductCardProps) {
  const activeShadow = '0 0 0 2px rgba(37,99,235,0.15)';
  const capabilities = product.capabilities ?? [];
  const users = product.users ?? [];
  const links = product.links ?? [];
  
  // Handle card click to set active product
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on buttons or links
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    // Set as active product if not already active and handler is provided
    if (onSetActive && !isActive) {
      onSetActive();
    }
  };

  return (
    <div 
      style={{
        border: isActive ? '2px solid #2563eb' : '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        background: isActive ? '#f8fafc' : 'white',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        boxShadow: isActive ? activeShadow : 'none',
        cursor: onSetActive && !isActive ? 'pointer' : 'default',
      }}
      onClick={handleCardClick}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)')}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = isActive ? activeShadow : 'none')}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          {isActive && (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                background: '#2563eb',
                color: 'white',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 600,
                marginBottom: '6px',
              }}
            >
              Active Product
            </span>
          )}
          <h3 style={{ margin: 0, marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>
            <span style={{ color: '#0066cc', fontFamily: 'monospace' }}>{product.productId}</span>
            {': '}
            {product.title}
          </h3>
          <div style={{ fontSize: '13px', color: '#666' }}>
            <strong>Owner:</strong> {product.owner}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(product);
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#666',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Edit product"
          >
            <Pencil size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(product);
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Delete product"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <p style={{ fontSize: '14px', color: '#444', marginBottom: '16px', lineHeight: '1.5' }}>
        {product.description}
      </p>

      {product.repositoryUrl && (
        <div style={{ 
          marginBottom: '16px',
          padding: '12px',
          background: '#f0f9ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Github size={16} style={{ color: '#0066cc' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#0066cc', marginBottom: '2px' }}>
              GitHub Repository
            </div>
            <a
              href={product.repositoryUrl.startsWith('http') ? product.repositoryUrl : `https://github.com/${product.repositoryUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '13px',
                color: '#374151',
                textDecoration: 'none',
                fontFamily: 'monospace'
              }}
            >
              {product.repositoryUrl}
            </a>
            {product.repoPath && (
              <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>
                Repo path:{' '}
                <span style={{ fontFamily: 'monospace' }}>
                  {product.repoPath}
                </span>
              </div>
            )}
            {localPath && (
              <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>
                Local clone:{' '}
                <span style={{ fontFamily: 'monospace' }}>
                  {localPath}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {capabilities.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
            Capabilities
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {capabilities.map((capability, index) => (
              <span
                key={index}
                style={{
                  background: '#e5e7eb',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#374151'
                }}
              >
                {capability}
              </span>
            ))}
          </div>
        </div>
      )}

      {users.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
            User Roles
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {users.map((user, index) => (
              <span
                key={index}
                style={{
                  background: 'white',
                  border: '1px solid #d1d5db',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#374151'
                }}
              >
                {user}
              </span>
            ))}
          </div>
        </div>
      )}

      {links.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
            Links
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {links.map((link, index) => (
              <a
                key={index}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '13px',
                  color: '#0066cc',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <ExternalLink size={12} />
                {link}
              </a>
            ))}
          </div>
        </div>
      )}

      <div style={{ 
        fontSize: '11px', 
        color: '#999', 
        paddingTop: '12px', 
        borderTop: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px'
      }}>
        <div>Created: {new Date(product.createdAt).toLocaleString()}</div>
        <div>Updated: {new Date(product.updatedAt).toLocaleString()}</div>
      </div>
    </div>
  );
}

